import React from 'react';
import { formatKeySequence } from '../hooks/useCommandMenu.js';
import './CmdMenu/CmdMenu.css';
import CommandIcon from './icons/command.svg?react';
import ShiftIcon from './icons/shiftkey.svg?react';
import OptionIcon from './icons/optionkey.svg?react';

/**
 * KeyboardShortcut - Reusable component for rendering keyboard shortcuts
 * 
 * @param {string|Array} sequence - Key sequence string (e.g., "1", "g s", "Meta+k", "/") or parsed array from formatKeySequence
 * @param {string} className - Optional additional CSS classes
 */
const KeyboardShortcut = ({ sequence, className = '' }) => {
  // Helper to render a single key with icon if it's a modifier
  const renderKey = (keyObj) => {
    if (keyObj.type === 'modifier') {
      let ModifierIcon = null;
      if (keyObj.display === 'command') ModifierIcon = CommandIcon;
      else if (keyObj.display === 'shift') ModifierIcon = ShiftIcon;
      else if (keyObj.display === 'option') ModifierIcon = OptionIcon;
      
      if (ModifierIcon) {
        return (
          <kbd className="cmdk-key cmdk-key-icon">
            <span className="inline-flex items-center justify-center w-[12px] h-[12px] flex-shrink-0">
              <ModifierIcon className="w-[12px] h-[12px]" aria-hidden="true" />
            </span>
          </kbd>
        );
      }
      // Fallback for Ctrl (text) - wider for multi-char
      return (
        <kbd className="cmdk-key cmdk-key-text-long">
          {keyObj.display}
        </kbd>
      );
    }
    
    // Regular key - check if it's a longer text key
    const isLongKey = keyObj.display.length > 1;
    return (
      <kbd className={`cmdk-key ${isLongKey ? 'cmdk-key-text-long' : 'cmdk-key-text'}`}>
        {keyObj.display}
      </kbd>
    );
  };

  // Parse the sequence if it's a string, otherwise use it directly
  const parsedSequence = typeof sequence === 'string' ? formatKeySequence(sequence) : sequence;

  return (
    <span className={`inline-flex items-center gap-1 ${className}`} aria-hidden="true">
      {parsedSequence.map((keyOrCombo, idx, arr) => (
        <span key={idx} className="inline-flex items-center gap-1">
          {Array.isArray(keyOrCombo) ? (
            // Modifier combination like [{type: 'modifier', key: 'Meta'}, {type: 'key', key: 'k'}]
            keyOrCombo.map((k, i) => (
              <React.Fragment key={i}>
                {renderKey(k)}
                {i < keyOrCombo.length - 1 && <span className="text-secondary text-xs mx-0.5">+</span>}
              </React.Fragment>
            ))
          ) : (
            // Single key
            renderKey(keyOrCombo)
          )}
          {idx < arr.length - 1 && <span className="px-1 text-xs text-secondary">then</span>}
        </span>
      ))}
    </span>
  );
};

export default React.memo(KeyboardShortcut);

